<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
  UiDialog,
  UiCurrencyInput,
  UiTextarea,
  UiText,
  UiButton,
  UiSwitch,
  UiInfoBox,
} from "@shopexperts/shared";
import type { AdminWalletTransaction } from "@shopexperts/legacy-api";
import { useReleaseAdminWalletHold } from "@/app/composables/wallet";
import { formatCurrency } from "@/v2Projects/utils/display";
import { centsToDollars, dollarsToCents } from "@/app/composables/expert-levels/expertLevelForm";

const props = defineProps<{
  transaction: AdminWalletTransaction | null;
  organizationId?: number | null;
}>();

const emit = defineEmits<{
  (e: "released"): void;
}>();

const open = defineModel<boolean>({ default: false });

const { mutateAsync: releaseHold, isPending } = useReleaseAdminWalletHold();

const partial = ref(false);
const amountDollars = ref<number>(0);
const notes = ref("");
const errorMessage = ref<string | null>(null);

const remainingCents = computed(() => props.transaction?.remaining_hold_cents ?? 0);
const remainingLabel = computed(() =>
  formatCurrency(remainingCents.value, props.transaction?.currency ?? null),
);

watch(open, (isOpen) => {
  if (isOpen) {
    partial.value = false;
    amountDollars.value = centsToDollars(remainingCents.value);
    notes.value = "";
    errorMessage.value = null;
  }
});

watch(partial, (isPartial) => {
  if (!isPartial) {
    amountDollars.value = centsToDollars(remainingCents.value);
  }
});

async function submit() {
  errorMessage.value = null;
  if (!props.transaction) return;

  let amountCents: number | undefined;
  if (partial.value) {
    amountCents = dollarsToCents(amountDollars.value);
    if (!Number.isFinite(amountCents) || amountCents < 1) {
      errorMessage.value = "Enter an amount greater than zero.";
      return;
    }
    if (amountCents > remainingCents.value) {
      errorMessage.value = `Amount can't exceed the remaining hold (${remainingLabel.value}).`;
      return;
    }
  }

  try {
    await releaseHold({
      transactionId: props.transaction.id,
      organizationId: props.organizationId ?? props.transaction.organization_id,
      body: {
        ...(amountCents !== undefined ? { amount_cents: amountCents } : {}),
        internal_notes: notes.value.trim() || null,
      },
    });
    open.value = false;
    emit("released");
  } catch {
    // Error toast handled by the composable.
  }
}

function cancel() {
  open.value = false;
}
</script>

<template>
  <UiDialog v-model="open" v-model:loading="isPending" title="Release hold" size="lg">
    <div class="flex flex-col gap-5">
      <UiText
        as="p"
        variant="p-small"
        class="text-se-gray-600"
        :text="`Releasing held funds moves them into the available balance. Remaining hold on this transaction: ${remainingLabel}.`"
      />

      <div class="flex items-center justify-between gap-3 rounded-xl border border-se-gray-200 px-4 py-3">
        <div class="flex flex-col">
          <UiText as="span" variant="p-small" class="font-medium text-se-dark" text="Partial release" />
          <UiText
            as="span"
            variant="caption-normal"
            class="text-se-gray-500"
            text="Off releases the full remaining hold."
          />
        </div>
        <UiSwitch v-model="partial" :disabled="isPending" />
      </div>

      <div v-if="partial" class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Amount to release" />
        <UiCurrencyInput v-model="amountDollars" :min="0" :step="1" :disabled="isPending" />
      </div>

      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Internal note (admin only)" />
        <UiTextarea
          v-model="notes"
          :rows="2"
          placeholder="Optional reason for releasing the hold."
          :disabled="isPending"
        />
      </div>

      <UiInfoBox variant="info">
        <UiText as="span" variant="p-small">
          <span>This action is recorded as a hold-release transaction on the wallet ledger.</span>
        </UiText>
      </UiInfoBox>

      <UiText v-if="errorMessage" as="p" variant="p-small" class="text-red-600" :text="errorMessage" />
    </div>

    <template #footer>
      <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <UiButton variant="outline" label="Cancel" :disabled="isPending" @click="cancel" />
        <UiButton
          variant="primary"
          :label="isPending ? 'Releasing…' : 'Release hold'"
          :disabled="isPending"
          @click="submit"
        />
      </div>
    </template>
  </UiDialog>
</template>
